import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Loader2, Lock, Eye, EyeOff, Shield, Users, Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import GoogleReCAPTCHA from '../Google/reCAPTCHA';

const ForgotPassword = () => {
  const { forgotPassword, changePasswordWithOld } = useAuth();
  const [activeTab, setActiveTab] = useState('email'); // 'email' or 'old-password'
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  
  // Old password form state
  const [oldPasswordForm, setOldPasswordForm] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // reCAPTCHA state
  const [emailRecaptchaToken, setEmailRecaptchaToken] = useState(null);
  const [oldPasswordRecaptchaToken, setOldPasswordRecaptchaToken] = useState(null);

  // Handle email form submission
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email is required');
      return;
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    // Check reCAPTCHA verification (skip if disabled)
    if (emailRecaptchaToken !== 'DISABLED' && !emailRecaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      console.log('🔑 Requesting password reset...', email);
      const result = await forgotPassword(email, emailRecaptchaToken);
      
      if (result.success) {
        console.log('✅ Password reset email sent successfully');
        setIsSubmitted(true);
      } else {
        console.log('❌ Password reset failed:', result.message);
        setError(result.message);
      }
    } catch (err) {
      console.log('❌ Password reset error:', err);
      setError('Failed to send reset email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle old password form submission
  const handleOldPasswordSubmit = async (e) => {
    e.preventDefault();
    
    const { email, currentPassword, newPassword, confirmPassword } = oldPasswordForm;
    
    if (!email || !currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Check reCAPTCHA verification (skip if disabled)
    if (oldPasswordRecaptchaToken !== 'DISABLED' && !oldPasswordRecaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      console.log('🔑 Changing password with old password...', email);
      const result = await changePasswordWithOld({
        email,
        currentPassword,
        newPassword,
        recaptchaToken: oldPasswordRecaptchaToken
      });
      
      if (result.success) {
        console.log('✅ Password changed successfully');
        setIsSubmitted(true);
      } else {
        console.log('❌ Password change failed:', result.message);
        setError(result.message);
      }
    } catch (err) {
      console.log('❌ Password change error:', err);
      setError('Failed to change password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input change for email form
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  // Handle input change for old password form
  const handleOldPasswordChange = (e) => {
    const { name, value } = e.target;
    setOldPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-green-400/20 to-emerald-400/20 dark:from-green-500/10 dark:to-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-400/20 to-purple-400/20 dark:from-blue-500/10 dark:to-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-green-300/10 to-blue-300/10 dark:from-green-400/5 dark:to-blue-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        {/* Back to Home Link */}
        <div className="absolute top-4 left-4 md:top-6 md:left-6 z-20">
          <Link
            to="/"
            className="inline-flex items-center px-3 py-2 md:px-4 md:py-2 text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-xl border border-white/20 dark:border-gray-700/50 hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            ← Back to Home
          </Link>
        </div>

        <div className="max-w-md w-full space-y-8 relative z-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 mb-6 shadow-lg">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              {activeTab === 'email' ? 'Check Your Email' : 'Password Changed'}
            </h2>
            <p className="mt-3 text-lg text-gray-600 dark:text-gray-300">
              {activeTab === 'email' 
                ? `We've sent a password reset link to ${email}`
                : 'Your password has been changed successfully'
              }
            </p>
          </div>

          <div className="card-3d bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-2xl rounded-3xl p-8 border border-white/20 dark:border-gray-700/50">
            {activeTab === 'email' ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                  <span>Check your email inbox</span>
                </div>
                <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                  <span>Click the reset link in the email</span>
                </div>
                <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                  <span>Create a new password</span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"></div>
                  <span>Your password has been updated</span>
                </div>
                <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"></div>
                  <span>You can now log in with your new password</span>
                </div>
              </div>
            )}

            <div className="mt-8 space-y-3">
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setEmail('');
                  setOldPasswordForm({
                    email: '',
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
                className="w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-2xl text-white bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Try another method
              </button>
              
              <Link
                to="/login"
                className="w-full flex justify-center py-4 px-6 border-2 border-gray-200/50 dark:border-gray-600/50 text-gray-700 dark:text-gray-300 font-semibold rounded-2xl hover:bg-gray-50/80 dark:hover:bg-gray-700/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 backdrop-blur-md"
              >
                Back to Login
              </Link>
            </div>
          </div>

          {activeTab === 'email' && (
            <div className="text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium transition-colors duration-200"
                >
                  try again
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 dark:from-blue-500/10 dark:to-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 dark:from-indigo-500/10 dark:to-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-300/10 to-purple-300/10 dark:from-blue-400/5 dark:to-purple-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Back to Home Link */}
      <div className="absolute top-6 left-6 z-20">
        <Link
          to="/"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-xl border border-white/20 dark:border-gray-700/50 hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          ← Back to Home
        </Link>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 mb-6 shadow-lg">
            <Mail className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            Forgot Password?
          </h2>
          <p className="mt-3 text-lg text-gray-600 dark:text-gray-300">
            Choose how you'd like to reset your password
          </p>
          
          {/* Feature Highlights */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Secure</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Reliable</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Modern</p>
            </div>
          </div>
        </div>

        {/* Main Form Container */}
        <div className="card-3d bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-2xl rounded-3xl p-8 border border-white/20 dark:border-gray-700/50">
          {/* Tab Navigation */}
          <div className="flex space-x-2 mb-8">
            <button
              onClick={() => setActiveTab('email')}
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-2xl transition-all duration-300 ${
                activeTab === 'email'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100/50 dark:bg-gray-700/50 hover:bg-gray-200/50 dark:hover:bg-gray-600/50'
              }`}
            >
              <Mail className="inline w-4 h-4 mr-2" />
              Email Reset
            </button>
            <button
              onClick={() => setActiveTab('old-password')}
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-2xl transition-all duration-300 ${
                activeTab === 'old-password'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-gray-100/50 dark:bg-gray-700/50 hover:bg-gray-200/50 dark:hover:bg-gray-600/50'
              }`}
            >
              <Lock className="inline w-4 h-4 mr-2" />
              Old Password
            </button>
          </div>

          {/* Email Reset Form */}
          {activeTab === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={handleEmailChange}
                    className={`block w-full pl-12 pr-4 py-4 border-2 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:border-gray-600/50 dark:text-white backdrop-blur-md transition-all duration-300 ${
                      error
                        ? 'border-red-400 dark:border-red-500 shadow-red-100 dark:shadow-red-900/20'
                        : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500'
                    }`}
                    placeholder="Enter your email address"
                  />
                </div>
                {error && (
                  <div className="flex items-center mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    {error}
                  </div>
                )}
              </div>

              {/* reCAPTCHA */}
              <div className="flex justify-center items-center">
              <div className="flex justify-center">
                <GoogleReCAPTCHA
                  page="forgotPassword"
                  onVerify={(token) => setEmailRecaptchaToken(token)}
                  onError={(errors) => {
                    console.error('reCAPTCHA errors:', errors);
                    setEmailRecaptchaToken(null);
                  }}
                />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || (emailRecaptchaToken !== 'DISABLED' && !emailRecaptchaToken)}
                  className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-2xl text-white bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <Loader2 className="animate-spin h-5 w-5 mr-3" />
                      Sending...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      Send Reset Link
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                    </div>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Old Password Form */}
          {activeTab === 'old-password' && (
            <form onSubmit={handleOldPasswordSubmit} className="space-y-6">
              <div>
                <label htmlFor="old-email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                  </div>
                  <input
                    id="old-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={oldPasswordForm.email}
                    onChange={handleOldPasswordChange}
                    className={`block w-full pl-12 pr-4 py-4 border-2 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:border-gray-600/50 dark:text-white backdrop-blur-md transition-all duration-300 ${
                      error
                        ? 'border-red-400 dark:border-red-500 shadow-red-100 dark:shadow-red-900/20'
                        : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500'
                    }`}
                    placeholder="Enter your email address"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="currentPassword" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Current Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                  </div>
                  <input
                    id="currentPassword"
                    name="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={oldPasswordForm.currentPassword}
                    onChange={handleOldPasswordChange}
                    className={`block w-full pl-12 pr-12 py-4 border-2 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:border-gray-600/50 dark:text-white backdrop-blur-md transition-all duration-300 ${
                      error
                        ? 'border-red-400 dark:border-red-500 shadow-red-100 dark:shadow-red-900/20'
                        : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500'
                    }`}
                    placeholder="Enter your current password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  New Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                  </div>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={oldPasswordForm.newPassword}
                    onChange={handleOldPasswordChange}
                    className={`block w-full pl-12 pr-12 py-4 border-2 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:border-gray-600/50 dark:text-white backdrop-blur-md transition-all duration-300 ${
                      error
                        ? 'border-red-400 dark:border-red-500 shadow-red-100 dark:shadow-red-900/20'
                        : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500'
                    }`}
                    placeholder="Enter your new password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Confirm New Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={oldPasswordForm.confirmPassword}
                    onChange={handleOldPasswordChange}
                    className={`block w-full pl-12 pr-12 py-4 border-2 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:border-gray-600/50 dark:text-white backdrop-blur-md transition-all duration-300 ${
                      error
                        ? 'border-red-400 dark:border-red-500 shadow-red-100 dark:shadow-red-900/20'
                        : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500'
                    }`}
                    placeholder="Confirm your new password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" />
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-center text-sm text-red-600 dark:text-red-400 font-medium">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  {error}
                </div>
              )}

              {/* reCAPTCHA */}
              <div className="flex justify-center items-center">
              <div className="flex justify-center">
                <GoogleReCAPTCHA
                  page="forgotPassword"
                  onVerify={(token) => setOldPasswordRecaptchaToken(token)}
                  onError={(errors) => {
                    console.error('reCAPTCHA errors:', errors);
                    setOldPasswordRecaptchaToken(null);
                  }}
                />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || !oldPasswordRecaptchaToken}
                  className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-2xl text-white bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <Loader2 className="animate-spin h-5 w-5 mr-3" />
                      Changing...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      Change Password
                      <Lock className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                    </div>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Divider */}
          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-gray-200/50 dark:border-gray-600/50" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md text-gray-500 dark:text-gray-400 font-medium">
                  Remember your password?
                </span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="w-full flex justify-center items-center py-4 px-6 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-2xl text-base font-semibold text-gray-700 dark:text-gray-300 bg-white/50 dark:bg-gray-700/50 hover:bg-gray-50/80 dark:hover:bg-gray-600/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 backdrop-blur-md"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Back to Login
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="text-blue-600 dark:text-blue-400 hover:underline font-medium transition-colors duration-200"
            >
              Sign up here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 